import { createTheme } from '@mui/material/styles';

const sharedTypography = {
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  h2: {
    fontWeight: 800,
    letterSpacing: '-0.03em',
  },
  h3: {
    fontWeight: 800,
    letterSpacing: '-0.03em',
  },
  h4: {
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },
  h5: {
    fontWeight: 700,
  },
  body1: {
    lineHeight: 1.7,
  },
  body2: {
    lineHeight: 1.65,
  },
  button: {
    fontWeight: 700,
    textTransform: 'none',
  },
};

function getPalette(mode) {
  if (mode === 'light') {
    return {
      mode: 'light',
      primary: {
        main: '#117FA4',
        contrastText: '#F7FBFF',
      },
      secondary: {
        main: '#E09A2D',
      },
      success: {
        main: '#138A58',
      },
      error: {
        main: '#C94747',
      },
      warning: {
        main: '#B87922',
      },
      background: {
        default: '#EEF4FA',
        paper: 'rgba(255, 255, 255, 0.92)',
      },
      text: {
        primary: '#0E1726',
        secondary: 'rgba(14, 23, 38, 0.72)',
      },
      divider: 'rgba(77, 99, 125, 0.16)',
    };
  }

  return {
    mode: 'dark',
    primary: {
      main: '#58C4DD',
      contrastText: '#06111D',
    },
    secondary: {
      main: '#F5C36B',
      contrastText: '#101520',
    },
    success: {
      main: '#39D98A',
    },
    error: {
      main: '#FF6B6B',
    },
    warning: {
      main: '#F5C36B',
    },
    background: {
      default: '#06111D',
      paper: 'rgba(12, 21, 37, 0.9)',
    },
    text: {
      primary: '#F4F8FC',
      secondary: 'rgba(223, 232, 241, 0.76)',
    },
    divider: 'rgba(126, 144, 168, 0.16)',
  };
}

export function getAppTheme(mode) {
  const palette = getPalette(mode);
  const isDark = mode === 'dark';

  return createTheme({
    palette,
    typography: sharedTypography,
    shape: {
      borderRadius: 18,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            minHeight: '100%',
            backgroundColor: palette.background.default,
          },
          body: {
            minHeight: '100%',
            background: isDark
              ? 'radial-gradient(circle at top left, rgba(88, 196, 221, 0.12), transparent 22%), radial-gradient(circle at top right, rgba(245, 195, 107, 0.08), transparent 18%), #06111D'
              : 'radial-gradient(circle at top left, rgba(17, 127, 164, 0.08), transparent 24%), radial-gradient(circle at top right, rgba(224, 154, 45, 0.08), transparent 18%), #EEF4FA',
            color: palette.text.primary,
          },
          '#root': {
            minHeight: '100vh',
            background: 'transparent',
          },
          '*, *::before, *::after': {
            boxSizing: 'border-box',
          },
          a: {
            color: 'inherit',
          },
          '::selection': {
            backgroundColor: isDark ? 'rgba(88, 196, 221, 0.28)' : 'rgba(17, 127, 164, 0.18)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            paddingInline: 18,
          },
          containedPrimary: {
            boxShadow: isDark ? '0 12px 30px rgba(88, 196, 221, 0.18)' : '0 10px 24px rgba(17, 127, 164, 0.18)',
          },
          outlinedInherit: {
            borderColor: isDark ? 'rgba(223, 232, 241, 0.18)' : 'rgba(14, 23, 38, 0.16)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 600,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
    },
  });
}
